import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { Minus, Plus, Trash2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { redirectToStripeCheckout } from '../services/stripeService';
import PageSEO from '../components/SEO/PageSEO';

const SHIPPING_COST = 3.99;

const CartPage = () => {
  const { state, dispatch } = useCart();

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) return;
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  };

  const removeItem = (id: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  };

  const handleCheckout = () => {
    redirectToStripeCheckout();
  };

  // Schéma JSON-LD pour la page panier
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemPage",
    "name": "Votre panier - Comprendre les SES",
    "description": "Consultez et modifiez les articles dans votre panier avant de finaliser votre commande.",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": state.items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": item.title,
          "offers": {
            "@type": "Offer",
            "price": item.price.toFixed(2),
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
          }
        }
      }))
    }
  };

  if (state.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageSEO
          title="Votre panier - Comprendre les SES"
          description="Votre panier est actuellement vide. Découvrez nos produits pour commencer vos révisions."
          path="/cart"
          image="https://cours-ses.fr/Logo SES.png"
        />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-[#323c5f] mb-4">Votre panier est vide</h1>
              <p className="text-gray-600 mb-8">Découvrez nos produits et commencez vos révisions !</p>
              <Link
                to="/flashcards"
                className="inline-block bg-[#9a6ebd] text-white px-6 py-3 rounded-md hover:bg-[#8a5dad] transition-colors"
              >
                Voir nos flashcards
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const total = state.total + SHIPPING_COST;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageSEO
        title="Votre panier - Comprendre les SES"
        description="Consultez et modifiez les articles dans votre panier avant de finaliser votre commande."
        path="/cart"
        image="https://cours-ses.fr/Logo SES.png"
        schema={schema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-2xl font-bold text-[#323c5f] mb-8">Votre panier</h1>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              {state.items.map((item) => (
                <div
                  key={item.id}
                  className="bg-white p-6 rounded-lg shadow-md mb-4 flex items-center"
                >
                  <div className="flex-grow">
                    <h3 className="font-semibold text-[#323c5f]">{item.title}</h3>
                    <p className="text-gray-600">{item.price.toFixed(2)}€</p>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-1 rounded-md hover:bg-gray-100"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1 rounded-md hover:bg-gray-100"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md h-fit">
              <h2 className="text-xl font-semibold text-[#323c5f] mb-4">Récapitulatif</h2>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span>Sous-total</span>
                  <span>{state.total.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between">
                  <span>Livraison</span>
                  <span>{SHIPPING_COST.toFixed(2)}€</span>
                </div>
                <div className="border-t pt-2 font-semibold flex justify-between">
                  <span>Total</span>
                  <span>{total.toFixed(2)}€</span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full bg-[#9a6ebd] text-white px-6 py-3 rounded-md hover:bg-[#8a5dad] transition-colors"
              >
                Commander
              </button>

              <div className="mt-4 text-sm text-gray-600">
                <p className="flex items-center justify-center">
                  🔒 Paiement 100% sécurisé
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;